import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, RefreshCw, Settings as SettingsIcon, Bell, Inbox as InboxIcon, ChevronLeft, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import VoiceTextarea from "../components/VoiceTextarea";
import { useBranding } from "../context/TenantBrandingContext";

// Tiny inline Markdown renderer — same scope as Hidro chat.
const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
const boldRe = /\*\*([^*]+)\*\*/g;

function renderInline(line, key) {
  const out = [];
  let cursor = 0;
  let m;
  linkRe.lastIndex = 0;
  while ((m = linkRe.exec(line)) !== null) {
    if (m.index > cursor) out.push(line.slice(cursor, m.index));
    const href = m[2];
    out.push(
      href.startsWith("/") ? (
        <Link key={`${key}-l-${m.index}`} to={href} className="text-[#1FB6C1] underline underline-offset-2 hover:text-[#A78BFA]">{m[1]}</Link>
      ) : (
        <a key={`${key}-l-${m.index}`} href={href} target="_blank" rel="noreferrer" className="text-[#1FB6C1] underline underline-offset-2">{m[1]}</a>
      ),
    );
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) out.push(line.slice(cursor));
  // Apply bold to string fragments
  return out.map((p, i) => {
    if (typeof p !== "string") return p;
    const sub = [];
    let last = 0;
    let mm;
    boldRe.lastIndex = 0;
    while ((mm = boldRe.exec(p)) !== null) {
      if (mm.index > last) sub.push(p.slice(last, mm.index));
      sub.push(<strong key={`${key}-b-${i}-${mm.index}`}>{mm[1]}</strong>);
      last = mm.index + mm[0].length;
    }
    if (last < p.length) sub.push(p.slice(last));
    return <span key={`${key}-w-${i}`}>{sub}</span>;
  });
}

function MarkdownLite({ text }) {
  const lines = useMemo(() => (text || "").split("\n"), [text]);
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 marker:text-[#1FB6C1]">
        {bullets.map((b, i) => (
          <li key={i} className="text-sm leading-relaxed">{renderInline(b, `b-${blocks.length}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };
  lines.forEach((ln, i) => {
    const t = ln.trimEnd();
    if (/^\s*[-*]\s+/.test(t)) {
      bullets.push(t.replace(/^\s*[-*]\s+/, ""));
      return;
    }
    flushBullets();
    if (!t.trim()) {
      blocks.push(<div key={`sp-${i}`} className="h-2" />);
      return;
    }
    if (t.startsWith("# ")) {
      blocks.push(<h1 key={`h-${i}`} className="text-xl font-display font-bold text-white">{renderInline(t.slice(2), `h-${i}`)}</h1>);
    } else if (t.startsWith("## ")) {
      blocks.push(<h2 key={`h-${i}`} className="text-lg font-display font-bold text-white mt-2">{renderInline(t.slice(3), `h-${i}`)}</h2>);
    } else {
      blocks.push(<p key={`p-${i}`} className="text-sm leading-relaxed text-[#d1d5db]">{renderInline(t, `p-${i}`)}</p>);
    }
  });
  flushBullets();
  return <div className="space-y-2">{blocks}</div>;
}

export default function Inbox() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [activeId, setActiveId] = useState(focus || null);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = async (opts) => {
    const silent = !!opts?.silent;
    if (!silent) setBusy(true);
    try {
      const { data } = await api.get("/notifications?limit=200");
      const onlyBriefs = (data?.items || []).filter((n) => n.kind === "daily_brief");
      setItems(onlyBriefs);
      if (!activeId && onlyBriefs.length) setActiveId(onlyBriefs[0].id);
    } finally {
      // Iter 173 — silent polls do not toggle the loading spinner so
      // the brief reader isn't yanked while the user is mid-sentence.
      if (!silent) setBusy(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/hidro/brief/settings");
      setSettings(data);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { load(); loadSettings(); /* eslint-disable-next-line */ }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });

  const runNow = async (mode = "morning") => {
    setRunning(true);
    try {
      const { data } = await api.post(`/hidro/brief/run?mode=${encodeURIComponent(mode)}`);
      // Add to top
      setItems((it) => [data, ...it]);
      setActiveId(data.id);
      // The body is filled async in the background — poll briefly so the
      // reader pane swaps the placeholder with the real brief.
      if (data.ready === false || /Generating|Composing/i.test(data.body || "")) {
        let tries = 0;
        const poll = async () => {
          tries += 1;
          try {
            const r = await api.get("/notifications?limit=200");
            const onlyBriefs = (r.data?.items || []).filter((n) => n.kind === "daily_brief");
            setItems(onlyBriefs);
            const fresh = onlyBriefs.find((x) => x.id === data.id);
            if (fresh && fresh.ready !== false && !/Generating|Composing/i.test(fresh.body || "")) return;
          } catch (_e) { /* noop */ }
          if (tries < 20) setTimeout(poll, 5000);
        };
        setTimeout(poll, 5000);
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not generate brief.");
    } finally {
      setRunning(false);
    }
  };

  const saveSettings = async (next) => {
    try {
      const { data } = await api.put("/hidro/brief/settings", next);
      setSettings(data);
    } catch (_e) { /* noop */ }
  };

  const active = items.find((x) => x.id === activeId);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); } catch (_e) { /* noop */ }
    setItems((it) => it.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
  };

  useEffect(() => {
    if (active && !active.is_read) markRead(active.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  return (
    <div className="hv-page-padding pb-12" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0"><InboxIcon className="w-5 h-5" /></div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl md:text-4xl font-bold leading-none truncate">Inbox</h1>
            <div className="text-[11px] text-[#647087] mt-0.5 truncate">Daily briefs from Hidro · {user?.first_name || user?.name || "you"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => runNow("morning")} disabled={running} className="hv-btn hv-btn-primary text-xs" data-testid="inbox-run-now">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} <span className="hidden xs:inline">Morning brief</span><span className="xs:hidden">Brief</span>
          </button>
          <button type="button" onClick={() => runNow("recap")} disabled={running} className="hv-btn hv-btn-ghost text-xs" data-testid="inbox-run-recap">
            <Sparkles className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Evening recap</span><span className="xs:hidden">Recap</span>
          </button>
          <button type="button" onClick={() => setShowSettings((v) => !v)} className="hv-btn hv-btn-ghost text-xs" data-testid="inbox-settings-btn">
            <SettingsIcon className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={load} className="hv-btn hv-btn-ghost text-xs" data-testid="inbox-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {showSettings && settings && (
        <div className="hv-card p-4 mb-4 border-[#1FB6C1]/30" data-testid="inbox-settings-panel">
          <div className="text-xs uppercase tracking-[0.2em] text-[#647087] mb-2">Auto-brief settings</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={!!settings.brief_enabled} onChange={(e) => saveSettings({ brief_enabled: e.target.checked })} data-testid="inbox-toggle-enabled" />
              <span>Auto-deliver each morning</span>
            </label>
            <label className="text-sm flex items-center gap-2">
              <span className="text-[#647087]">Morning</span>
              {/* [Iter 290zp.21] Switched to onBlur commit so mobile
                  wheel scrolls don't fire saveSettings on every tick —
                  same pattern as HVDateInput. */}
              <input
                type="time"
                defaultValue={settings.brief_time || "06:00"}
                key={`brief-${settings.brief_time || "06:00"}`}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v && v !== settings.brief_time) saveSettings({ brief_time: v });
                }}
                className="hv-input text-sm w-28"
                data-testid="inbox-time-input"
              />
            </label>
            <label className="text-sm flex items-center gap-2">
              <span className="text-[#647087]">Evening recap</span>
              <input
                type="time"
                defaultValue={settings.recap_time || "18:00"}
                key={`recap-${settings.recap_time || "18:00"}`}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v && v !== settings.recap_time) saveSettings({ recap_time: v });
                }}
                className="hv-input text-sm w-28"
                data-testid="inbox-recap-input"
              />
            </label>
            <div className="text-[11px] text-[#647087] flex items-center">
              SAST · Africa/Johannesburg
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3">
        {/* List — hidden on mobile when a brief is open (master/detail). */}
        <div className={`hv-card p-0 overflow-hidden ${active ? "hidden md:block" : ""}`}>
          <div className="px-3 py-2 border-b border-white/10 text-xs uppercase tracking-[0.2em] text-[#647087] flex items-center gap-1.5">
            <Bell className="w-3 h-3" /> Briefs ({items.length})
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5" data-testid="inbox-list">
            {busy ? (
              <div className="text-xs text-[#647087] p-4 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-xs text-[#647087] p-4 text-center">
                No briefs yet. Click <em>Run brief now</em>.
              </div>
            ) : items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setActiveId(n.id)}
                className={`w-full text-left px-3 py-2 ${n.id === activeId ? "bg-[#1FB6C1]/[0.08]" : ""} ${n.is_read ? "" : "border-l-2 border-[#1FB6C1]"} hover:bg-white/[0.03]`}
                data-testid={`inbox-item-${n.id}`}
              >
                <div className="text-xs font-medium truncate text-white">{n.title}</div>
                <div className="text-[10px] text-[#647087] mt-0.5">
                  {new Date(n.created_at).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={`hv-card p-5 min-h-[40vh] ${active ? "" : "hidden md:block"}`} data-testid="inbox-reader">
          {active ? (
            <>
              {/* Mobile-only Back button — switches the master/detail flip back to the list. */}
              <button
                type="button"
                onClick={() => setActiveId(null)}
                className="md:hidden hv-btn hv-btn-ghost text-xs mb-3"
                data-testid="inbox-back-to-list"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Briefs ({items.length})
              </button>
              <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Hidro daily brief</div>
                  <div className="font-display text-lg font-bold mt-0.5">{active.title}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="text-[10px] text-[#647087] whitespace-nowrap">
                    {new Date(active.created_at).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}
                  </div>
                  <Link
                    to="/settings#brief-coaching"
                    className="hv-btn hv-btn-ghost text-[11px] text-[#1FB6C1] hover:bg-[#1FB6C1]/[0.08]"
                    data-testid="brief-coaching-shortcut"
                    title="Tell Hidro what kind of briefs you want — applied to every future brief."
                  >
                    <MessageSquare className="w-3 h-3" /> Coach Hidro
                  </Link>
                </div>
              </div>
              <MarkdownLite text={active.body} />
              <BriefFeedbackBar
                brief={active}
                onRegenStart={() => {
                  // Optimistic: blank the body so user sees the spinner.
                  setItems((arr) => arr.map((n) => (
                    n.id === active.id ? { ...n, body: "_⏳ Hidro is rewriting your brief…_", ready: false } : n
                  )));
                  // Poll for the rewrite (same cadence as runNow).
                  let tries = 0;
                  const poll = async () => {
                    tries += 1;
                    try {
                      const r = await api.get("/notifications?limit=200");
                      const onlyBriefs = (r.data?.items || []).filter((n) => n.kind === "daily_brief");
                      setItems(onlyBriefs);
                      const fresh = onlyBriefs.find((x) => x.id === active.id);
                      if (fresh && fresh.ready !== false && !/Generating|Composing|rewriting/i.test(fresh.body || "")) return;
                    } catch (_e) { /* noop */ }
                    if (tries < 20) setTimeout(poll, 5000);
                  };
                  setTimeout(poll, 4000);
                }}
              />
            </>
          ) : (
            <div className="text-sm text-[#647087] text-center py-12">
              {items.length === 0 ? "Click Run brief now to generate your first daily brief." : "Pick a brief on the left."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function BriefFeedbackBar({ brief, onRegenStart }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [rating, setRating] = useState(brief.feedback_rating || null);
  const [comment, setComment] = useState(brief.feedback_comment || "");
  const [showComment, setShowComment] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset state when the user switches to a different brief.
  useEffect(() => {
    setRating(brief.feedback_rating || null);
    setComment(brief.feedback_comment || "");
    setShowComment(false);
  }, [brief.id, brief.feedback_rating, brief.feedback_comment]);

  const isPending = brief.ready === false || /Generating|Composing|rewriting/i.test(brief.body || "");

  const submit = async (r, withComment) => {
    if (busy || isPending) return;
    setBusy(true);
    try {
      const body = { rating: r };
      if (withComment && comment.trim()) body.comment = comment.trim();
      const { data } = await api.post(`/hidro/brief/${brief.id}/feedback`, body);
      setRating(r);
      if (data?.regenerating) {
        toast.success(`${aiName} is rewriting your brief with your feedback…`);
        onRegenStart && onRegenStart();
        setShowComment(false);
      } else {
        toast.success(`Thanks — ${aiName} will keep learning.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save feedback.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-4 border-t border-white/10" data-testid="brief-feedback-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#647087] mr-1">
          Was this brief useful?
        </span>
        <button
          type="button"
          onClick={() => submit("up", false)}
          disabled={busy || isPending}
          className={`hv-btn text-xs disabled:opacity-50 ${
            rating === "up"
              ? "bg-emerald-500/15 border border-emerald-400/40 text-emerald-200"
              : "hv-btn-ghost"
          }`}
          data-testid="brief-feedback-up"
          title="Keep doing this"
        >
          <ThumbsUp className="w-3.5 h-3.5" /> Helpful
        </button>
        <button
          type="button"
          onClick={() => setShowComment((v) => !v)}
          disabled={busy || isPending}
          className={`hv-btn text-xs disabled:opacity-50 ${
            rating === "down"
              ? "bg-red-500/15 border border-red-400/40 text-red-200"
              : "hv-btn-ghost"
          }`}
          data-testid="brief-feedback-down"
          title="Tell Hidro what to fix — he'll rewrite this brief."
        >
          <ThumbsDown className="w-3.5 h-3.5" /> Coach Hidro
        </button>
        {rating && !showComment && (
          <span className="text-[11px] text-[#647087]">
            Saved · Hidro adapts future briefs from this.
          </span>
        )}
      </div>
      {showComment && (
        <div className="mt-3" data-testid="brief-feedback-form">
          <label htmlFor={`coach-${brief.id}`} className="block text-[11px] text-[#a0aabf] mb-1">
            What should be different? (Hidro will rewrite this brief now and learn for next time.)
          </label>
          <VoiceTextarea
            id={`coach-${brief.id}`}
            value={comment}
            onChange={setComment}
            rows={3}
            maxLength={1000}
            placeholder="e.g. Too long — keep it under 6 bullets. / Stop using emojis. / I want pipeline first, calendar last. / Focus on tomorrow's call list."
            data-testid="brief-feedback-comment"
           testIdPrefix="brief-feedback-comment-voice" context="note" />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setShowComment(false); }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="brief-feedback-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => submit("down", true)}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="brief-feedback-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
              Send & rewrite brief
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
